import { createSelector } from 'reselect';
import { isBlank } from 'utils/isBlank';
import { isPresent } from 'utils/isPresent';

import { parseQueryString } from 'components/Common/ParameterUtilities';
import { Feature } from 'components/PayWall/types';

import { SUCCESS_BAR, WARNING_BAR } from '../../Common/NoticeBar/BannerBar';
import { stringArrayToDisplayList } from '../utils/string';

import { DIALOG_ADD_ONS, DIALOG_PAYMENT_REVIEW } from './Actions';
import { DOWNGRADE_FEATURES_MESSAGES, DowngradeFeatureMessage } from './Data';
import {
  PayWallAvailablePlans,
  PayWallDescriptionItem,
  PayWallFullState,
  PayWallGroup,
  PayWallGroupIncrementalFeatures,
  PayWallOrgFeature,
  PayWallPlan,
  PayWallState,
} from './Reducer.type';

export const getSelectedPlan = (state: PayWallFullState) => state.payWall.selectedPlan;
export const getSelectedDuration = (state: PayWallFullState) => state.payWall.selectedDuration;
export const getAvailablePlans = (state: PayWallFullState) => state.payWall.availablePlans ?? {};
export const getTiers = (state: PayWallFullState) => state.payWall.tiers;
export const getPlanGroups = (state: PayWallFullState): PayWallGroup[] => state.payWall.planGroups;
export const getOrgFeatures = (state: PayWallFullState) => state.payWall.orgFeatures ?? [];
export const getOriginalOrgFeatures = (state: PayWallFullState) => state.payWall.originalOrgFeatures ?? [];
export const getRequiredPlan = (state: PayWallFullState) => state.payWall.required;
export const getOriginalCurrentPlan = (state: PayWallFullState) => state.payWall.originalCurrentPlan ?? {};
const getPlanFeatures = createSelector(getSelectedPlan, (selectedPlan) => selectedPlan.features ?? []);
const getAvailableFeatures = (state: PayWallFullState) => state.payWall.availableFeatures ?? [];
const getPaywall = (state: PayWallFullState): PayWallState => state.payWall;
export const getPaywallVariant = (state: PayWallFullState) => state.payWall.variant;

export const getOrgFeaturesToBeRemoved = createSelector(
  [getOrgFeatures, getOriginalOrgFeatures],
  (currentOrgFeatures, originalOrgFeatures) => {
    return originalOrgFeatures.filter(
      ({ name: orgFeature }) =>
        !currentOrgFeatures.some(({ name: originalOrgFeature }) => orgFeature === originalOrgFeature)
    );
  }
);

export const getDowngradeFeatureMessages = createSelector([getOrgFeaturesToBeRemoved], (orgFeaturesToBeRemoved) => {
  return orgFeaturesToBeRemoved
    .map((feature) => DOWNGRADE_FEATURES_MESSAGES.find((featureMessage) => feature.name === featureMessage.name))
    .filter((feature) => feature !== undefined) as DowngradeFeatureMessage[];
});

// TEMPORARY SELECTORS FOR V3 PAYWALL, REMOVE WHEN FULLY TRANSITIONED TO V3
export const getSelectedPlanV3 = (state: PayWallFullState) =>
  state.payWall.selectedPlan || (state.payWall.originalCurrentPlan as PayWallPlan);
export const getAvailablePlansV3 = (state: PayWallFullState) => state.payWall.plans ?? [];
export const getOriginalCurrentPlanV3 = (state: PayWallFullState) =>
  (state.payWall.originalCurrentPlan ?? {}) as PayWallPlan;
export const getDiscountLabel = createSelector(
  [getSelectedPlanV3, getAvailablePlansV3],
  (selectedPlan, availablePlans) => {
    // Set here makes sure only uniqe values will be included in the array
    const availablePlansDiscountLabels = [...new Set(availablePlans.map((plan) => plan.discount_label))];
    // If all available plans have the same discount label, show it right away regardless if any plan is selected
    if (availablePlansDiscountLabels.length === 1) {
      return availablePlansDiscountLabels[0];
    }

    return selectedPlan.discount_label;
  }
);

/** Backend representation of number of seats for unlimited plans */
const UNLIMITED_PLAN_SEATS = 0;

/**
 * External top tier addons are additional services included in a top tier plans that are not integrated
 * with our backend (as features) but should still be listed as an added benefit to chosing those plans.
 */
const externalTopTierAddons: PayWallOrgFeature[] = [
  {
    display_name: 'Website Builder',
    price: 5000,
    annual_price: '$50',
    monthly_price: '$50',
    six_month_price: '$50',
    id: 0,
    name: 'website_builder',
    quantifiable: false,
    quantity: undefined,
  },
];

export const ADDITIONAL_LOGINS_FEATURE_NAME = 'additional_employee';

/**
 * A list of features that should overwrite manual org features setting upon plan change.
 */
export const FEATURES_TO_OVERWRITE_ORG = ['sales_management'];

/**
 * This selector will return an array of plan groups with an addition of a `plan_descriptions_incremental`
 * and `addons_descriptions` properties in them.
 *
 * `plan_descriptions_incremental` value
 * is constructed based on the value of a preceding plan group `plan_descriptions`.
 *
 * `addons_descriptions` is calculated based on provided `availableFeatures`
 * in relation to `features` that each group has.
 *
 * Given such plan groups of X, Y and Z, that have a feature sets set as follows:
 * X: { plan_descriptions:  [A, B] }
 * Y: { plan_descriptions: [A, B, C, D] }
 * Z: { plan_descriptions: [A, B, C, D, E, F] }
 *
 * First group (X) shall always contain all of its features (A and B) and the following groups shall contain only
 * the features that are unique (additive) to it compared to the previous plan:
 *
 * X: { plan_descriptions_incremental: [A, B] }
 * Y: { plan_descriptions_incremental: [C, D] }
 * Z: { plan_descriptions_incremental: [E, F] }
 *
 * Originial `plan_descriptions` property shall remain intact (it is being in other places).
 */
export const getPlanGroupsWithIncrementalFeatures = createSelector(
  [getPlanGroups, getAvailablePlansV3],
  (groups, plans) =>
    groups
      .filter((group) => group.available_plans.length > 0)
      .reduce((filteredGroups, current, index) => {
        const isTopTier = index === groups.length - 1;

        let addonsDescriptions: PayWallDescriptionItem[] = [];
        const planForAddonsDescription = plans.find((plan) => plan.id === current.available_plans[0]);

        if (current.available_plans.length && planForAddonsDescription?.available_features) {
          addonsDescriptions = planForAddonsDescription.available_features
            .filter((feature) => feature.name !== ADDITIONAL_LOGINS_FEATURE_NAME)
            .filter((feature) => planForAddonsDescription.features.includes(feature.name))
            .concat(isTopTier ? externalTopTierAddons : [])
            .map((feature) => ({ title: feature.display_name, description: '' }));
        }

        if (index === 0) {
          const initialFeatures = [...current.plan_descriptions];
          initialFeatures.unshift({
            title: 'Features included:',
            description: '',
          });

          filteredGroups.push({
            ...current,
            plan_descriptions_incremental: initialFeatures,
            addons_descriptions: addonsDescriptions,
          });

          return filteredGroups;
        }

        const previousGroup = filteredGroups[index - 1];

        if (!previousGroup) {
          return [];
        }

        const previousGroupPlanDescriptions = previousGroup.plan_descriptions ?? [];

        // For all subsequent ones, we want only the ones that are suplementary,
        // so ones not included in the previous group
        const filteredGroupFeatures = current.plan_descriptions.filter(
          (feature) =>
            !previousGroupPlanDescriptions.find(
              (prevFeature) => prevFeature.title === feature.title && prevFeature.description === feature.description
            )
        );

        // We don't store display_names in the planGroups so we need to manually match them to the values from tiers
        const prevPlansNames = previousGroup.available_plans
          .map((id) => plans.find((plan) => plan.id === id)?.display_name)
          .filter(Boolean) as string[];

        filteredGroupFeatures.unshift({
          title: `All ${stringArrayToDisplayList(prevPlansNames, '&')} features and:`,
          description: '',
        });

        filteredGroups.push({
          ...current,
          plan_descriptions_incremental: filteredGroupFeatures ?? [],
          addons_descriptions: addonsDescriptions,
        });

        return filteredGroups;
      }, [] as PayWallGroupIncrementalFeatures[])
);

export const getPlansWithoutAddOns = createSelector(
  [getPlanGroupsWithIncrementalFeatures, getAvailablePlansV3],
  (groups, plans) => {
    const displayNames: string[] = groups
      .filter((group) => !group.addons_descriptions || group.addons_descriptions.length === 0)
      .map((group) => group.available_plans.map((id) => plans.find((plan) => plan.id === id)?.display_name))
      .flat()
      .filter(Boolean) as string[]; // get rid of undefined values (if any)

    return stringArrayToDisplayList(displayNames);
  }
);

const getCurrentPlanGroupFeatures = createSelector(getSelectedPlan, (selectedPlan) => {
  return selectedPlan?.features ?? [];
});

export const getCurrentPlanAvailableFeatures = createSelector(getSelectedPlan, (selectedPlan) => {
  return selectedPlan?.available_features ?? [];
});
// END TEMPORARY SELECTORS

export const SCALE_PLAN_TIER = 'large';

export enum PAYWALL_PLAN_SEATS {
  FREE = 1,
}

export const getPayWallAddonsFeatures = createSelector(
  [getPlanFeatures, getOrgFeatures, getAvailableFeatures],
  (planFeatures, orgFeatures, availableFeatures) => {
    type Feature = {
      enabled: boolean;
      featureName: string;
      displayName: string;
      price: number;
      included?: boolean;
    };

    const addons: Feature[] = [];

    availableFeatures.forEach((availableFeature) => {
      if (!isPresent(availableFeature.name)) {
        return;
      }

      const orgAvailableFeature = orgFeatures.find((orgFeature) => orgFeature.name === availableFeature.name);

      if (planFeatures.includes(availableFeature.name)) {
        return;
      }

      addons.push({
        enabled: Boolean(orgAvailableFeature),
        featureName: availableFeature.name,
        displayName: availableFeature.display_name,
        price: orgAvailableFeature ? orgAvailableFeature.price : availableFeature.price,
      });
    });

    return addons;
  }
);

export const getPayWallAddonsFeaturesV3 = createSelector(
  [getCurrentPlanGroupFeatures, getOrgFeatures, getCurrentPlanAvailableFeatures],
  (currentPlanGroupFeatures, orgFeatures, availableFeatures) => {
    const addons: Feature[] = [];

    availableFeatures.forEach((availableFeature) => {
      if (!isPresent(availableFeature.name)) {
        return;
      }

      if (availableFeature.name === ADDITIONAL_LOGINS_FEATURE_NAME) {
        return;
      }

      const orgAvailableFeature = orgFeatures.find((orgFeature) => orgFeature.name === availableFeature.name);

      let { price } = availableFeature;

      if (orgAvailableFeature && !FEATURES_TO_OVERWRITE_ORG.includes(availableFeature.name)) {
        price = orgAvailableFeature.price;
      }

      addons.push({
        enabled: Boolean(orgAvailableFeature),
        featureName: availableFeature.name,
        displayName: availableFeature.display_name,
        price,
        included: currentPlanGroupFeatures.includes(availableFeature.name),
        quantifiable: availableFeature.quantifiable,
        quantity: orgAvailableFeature ? orgAvailableFeature.quantity : 0,
        minimalQuantity: 0,
      });
    });

    return addons;
  }
);

export const getAdditionalLoginsFeature = createSelector(
  [getCurrentPlanAvailableFeatures],
  (currentPlanGroupAvailableFeatures) =>
    currentPlanGroupAvailableFeatures.find((feature) => feature.name === ADDITIONAL_LOGINS_FEATURE_NAME)
);

export const getPlanSummaryPreview = (state: PayWallFullState) => state.payWall.planSummaryPreview ?? {};

export const getActivationFeeToDisplay = createSelector(
  getPlanSummaryPreview,
  (planSummaryPreview) => planSummaryPreview.activation_fee_to_display
);

export const getSkipSetupFee = createSelector(
  getPlanSummaryPreview,
  (planSummaryPreview) => planSummaryPreview.skip_setup_fee
);

export const getTotalDueToday = createSelector(
  getPlanSummaryPreview,
  (planSummaryPreview) => planSummaryPreview.total_due_today
);

export const getRenewalDate = createSelector(
  getPlanSummaryPreview,
  (planSummaryPreview) => planSummaryPreview.renewal_date
);

export const getAdditionalUserFromPlanSummary = createSelector(
  getPlanSummaryPreview,
  (planSummaryPreview) => planSummaryPreview.additional_users
);

const getAdditionalUsersFromPlanPreview = createSelector(
  getAdditionalUserFromPlanSummary,
  (additionalUserFromPlanSummary) => additionalUserFromPlanSummary?.count
);

export const getAdditionalUsers = (state: PayWallFullState) => state.payWall.additionalUsers;
export const getOriginalAdditionalUsers = (state: PayWallFullState): number | null =>
  state.payWall.originalAdditionalUsers;
export const getAdditionalUserPrice = (state: PayWallFullState): number =>
  state.payWall.selectedPlan.additional_employee_rate ?? 0;
export const getAdditionalUserPriceV3 = createSelector(
  getAdditionalLoginsFeature,
  (additionalLoginsFeature) => additionalLoginsFeature?.price ?? 0
);
export const getNumberOfFreeSeats = createSelector(
  [getSelectedPlan],
  (selectedPlan) => selectedPlan.number_of_free_seats ?? 0
);
export const getHasUnlimitedSeats = createSelector(
  [getNumberOfFreeSeats],
  (freeSeatsNumber) => freeSeatsNumber === UNLIMITED_PLAN_SEATS
);
const getMonthlySaasPrice = createSelector(
  [getSelectedPlan],
  (selectedPlan): number => Number(selectedPlan.rate_flat_monthly) ?? 0
);
const get6MonthSaasPrice = createSelector(
  [getSelectedPlan],
  (selectedPlan): number => Number(selectedPlan.rate_flat_6_months) ?? 0
);
const getAnnualSaasPrice = createSelector(
  [getSelectedPlan],
  (selectedPlan): number => Number(selectedPlan.rate_flat_annual) ?? 0
);

const getTotalMonthlyPrice = (
  saasPrice = 0,
  additionalFeatures: PayWallOrgFeature[],
  additionalUsers = 0,
  additionalUsersPrice = 0
) => {
  let totalPrice = 0;

  totalPrice += Number(saasPrice);

  totalPrice += additionalUsers * (additionalUsersPrice / 100);

  const featuresPrice = additionalFeatures.reduce((prevVal, feature) => {
    const { price } = feature;

    return prevVal + price / 100;
  }, 0);

  totalPrice += featuresPrice;

  return totalPrice;
};

export const getFinalAdditionalUsers = createSelector(
  [getAdditionalUsers, getAdditionalUsersFromPlanPreview],
  (additionalUserFromUI, additionalUserFromPlanPreview) => additionalUserFromUI ?? additionalUserFromPlanPreview
);

export const getMonthlyPrice = createSelector(
  [getMonthlySaasPrice, getOrgFeatures, getFinalAdditionalUsers, getAdditionalUserPrice],
  getTotalMonthlyPrice
);

export const get6MonthPrice = createSelector(
  [get6MonthSaasPrice, getOrgFeatures, getFinalAdditionalUsers, getAdditionalUserPrice],
  getTotalMonthlyPrice
);

export const getAnnualPrice = createSelector(
  [getAnnualSaasPrice, getOrgFeatures, getFinalAdditionalUsers, getAdditionalUserPrice],
  getTotalMonthlyPrice
);

type UpdatePlanProps = [string, number, PayWallOrgFeature[], number];

export const getUpdatePlanProps = (state: PayWallFullState): UpdatePlanProps => [
  state.payWall.selectedDuration,
  state.payWall.selectedPlan.id,
  state.payWall.orgFeatures,
  getFinalAdditionalUsers(state),
];

export const shouldShowMonthlyPricing = createSelector(getMonthlySaasPrice, (monthlySaas) => monthlySaas > 0);

export const shouldShow6MonthPricing = createSelector(get6MonthSaasPrice, (sixMonthSaas) => sixMonthSaas > 0);

export const shouldShowAnnualPricing = createSelector(getAnnualSaasPrice, (annualSaas) => annualSaas > 0);

export const isCurrentPlanAvailable = createSelector(
  getAvailablePlans,
  getTiers,
  getOriginalCurrentPlan,
  (availablePlans, tiers, selectedPlan) => {
    const selectedPlanInTiers = tiers.find((tier) => tier.id === selectedPlan.id);
    const keys = Object.keys(availablePlans) as unknown as Array<keyof PayWallAvailablePlans>;
    const availablePlanIds = keys.map((key) => availablePlans[key].id);
    return availablePlanIds.includes(selectedPlan.id) || Boolean(selectedPlanInTiers);
  }
);

export const isOnLegacyPlan = createSelector(getOriginalCurrentPlan, (selectedPlan) => !selectedPlan.active);

export const getPlanTierFromUrl = createSelector(getAvailablePlans, getTiers, (availablePlans, tiers) => {
  const planTier = parseQueryString(window.location.search)['plan-tier'] as keyof PayWallAvailablePlans;
  const tierFromUrl = tiers?.find((tier) => tier.tier === planTier);

  if (tierFromUrl) {
    return tierFromUrl.tier;
  }

  return isPresent(availablePlans[planTier]) ? planTier : '';
});

export const getSelectedDurationFromUrl = createSelector(
  getPlanTierFromUrl,
  (planTier) => ((isPresent(planTier) && parseQueryString(window.location.search)['billing-cycle']) as string) || ''
);

export const getDialogStepFromUrl = createSelector(
  getPlanTierFromUrl,
  getSelectedDurationFromUrl,
  (planTier, selectedDuration) => {
    if (isPresent(selectedDuration)) return DIALOG_PAYMENT_REVIEW;
    if (isPresent(planTier)) return DIALOG_ADD_ONS;
    return '';
  }
);

export const getCanAddEmployee = createSelector(
  getOriginalCurrentPlan,
  (selectedPlan) => selectedPlan.can_add_employee
);

export const getCanAddAdditionalEmployee = createSelector(
  getOriginalCurrentPlan,
  (selectedPlan) => selectedPlan.can_add_additional_employee
);

export const getBannerMessage = createSelector(getPaywall, (paywall) =>
  isPresent(paywall.bannerMessage) ? paywall.bannerMessage : paywall.uiOverrideBannerMessage
);

export const getBannerType = createSelector(getPaywall, (paywall) => paywall.bannerType ?? WARNING_BAR);

export const getPaywallSpinnerState = createSelector(getPaywall, (paywall) => paywall.spinnerState);

export const getPaywallShowCreditCardState = createSelector(
  getPaywall,
  (paywall) => paywall.showCreditCard || isBlank(paywall.creditCard)
);

export const getExistingUsersCount = createSelector(getPaywall, (paywall) => paywall.existingUsersCount);

export const getAdditionalUserMonthlyRate = createSelector(getPaywall, (paywall) => paywall.additionalUserMonthlyRate);

export const shouldPersistBanner = createSelector(getBannerType, (bannerType) => bannerType === SUCCESS_BAR);

export const isUpsellDialogOpen = createSelector(getPaywall, (paywall) => paywall.isUpsellDialogOpen);

export const getIsDowngradingPlan = createSelector(
  [getOriginalCurrentPlan, getMonthlySaasPrice],
  (originalCurrentPlan, monthlySassPrice) => {
    const { rate_flat_monthly: rateFlatMonthly } = originalCurrentPlan;
    return Number(rateFlatMonthly) > monthlySassPrice;
  }
);

export const getIsUpgradingPlan = createSelector(
  [getOriginalCurrentPlan, getMonthlySaasPrice],
  (originalCurrentPlan, monthlySassPrice) => {
    const { rate_flat_monthly: rateFlatMonthly } = originalCurrentPlan;
    return Number(rateFlatMonthly) < monthlySassPrice;
  }
);

export const getOrganizationAddress = createSelector(getPaywall, (paywall) => paywall.organizationAddress);
